import { createGlobalStyle, css, DefaultTheme, ThemeProvider } from 'styled-components';
import { ComponentsProps, getTextTokenPresetObject, Theme, ThemeVariant } from '@optimus-web/common';
import { ClickAwayContextProvider } from '@optimus-web/helpers';
import { defaultTranslations, LocalizationProvider, LocalizationTexts } from '@optimus-web/localization';
import {
    createTheme,
    DENSE_B2B_DARK_THEME,
    DENSE_B2B_LIGHT_THEME,
    DENSE_GX_DARK_THEME,
    DENSE_GX_LIGHT_THEME,
} from '../theme';

export interface OptimusProviderProps {
    children: React.ReactNode;
    themeVariant?: ThemeVariant;
    localizationTexts?: LocalizationTexts;
    tokenOverrides?: Partial<Theme>;
    componentsProps?: Partial<ComponentsProps>;
}

const GlobalStyles = createGlobalStyle`
    html {
        ${({ theme }) => css`
            font-family: ${getTextTokenPresetObject('bodyMedium')({ theme }).fontFamily};
        `};
    }
    body {
        -moz-osx-font-smoothing: grayscale;
        -webkit-font-smoothing: antialiased;
    }
`;

const OptimusProvider = ({
    children,
    themeVariant = 'b2bLight',
    localizationTexts = defaultTranslations,
    tokenOverrides,
    componentsProps,
    ...other
}: OptimusProviderProps) => {
    let baseTheme;

    switch (themeVariant) {
        case 'b2bLight':
            baseTheme = DENSE_B2B_LIGHT_THEME;
            break;
        case 'b2bDark':
            baseTheme = DENSE_B2B_DARK_THEME;
            break;
        case 'gxLight':
            baseTheme = DENSE_GX_LIGHT_THEME;
            break;
        case 'gxDark':
            baseTheme = DENSE_GX_DARK_THEME;
            break;
        default:
            baseTheme = DENSE_B2B_LIGHT_THEME;
            break;
    }

    const finalTheme: DefaultTheme =
        tokenOverrides && themeVariant
            ? createTheme({ themeVariant, tokenOverrides, componentsProps })
            : {
                  ...baseTheme,
                  tokens: { ...baseTheme.tokens, ...tokenOverrides },
                  settings: {
                      ...baseTheme.settings,
                      componentsProps: { ...baseTheme.settings.componentsProps, ...componentsProps },
                  },
                  themeVariant,
              };

    return (
        <ThemeProvider theme={finalTheme} {...other}>
            <LocalizationProvider localizationTexts={localizationTexts}>
                <>
                    <GlobalStyles />
                    <ClickAwayContextProvider>{children}</ClickAwayContextProvider>
                </>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default OptimusProvider;
